// Progress overlay shown while an MRP run is calculating. `step` is 1-based: steps before it are
// done, `step` itself is in progress; step > steps.length means the run finished.

interface Props {
  open: boolean;
  steps: string[];
  step: number;
}

export default function MrpRunOverlay({ open, steps, step }: Props) {
  if (!open) return null;
  const complete = step > steps.length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/80 backdrop-blur-sm" role="status" aria-live="polite">
      <div className="bg-white border border-slate-200 rounded-xl shadow-2xl p-8 w-full max-w-sm mx-4 text-center">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${complete ? 'bg-emerald-50' : 'bg-indigo-50'}`}>
          {complete ? (
            <i className="ri-check-line text-2xl text-emerald-600" />
          ) : (
            <i className="ri-loader-4-line text-xl text-indigo-600 animate-spin" />
          )}
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">
          {complete ? 'MRP Run Complete' : 'Running MRP...'}
        </h3>
        <div className="space-y-3 mt-5 text-left">
          {steps.map((label, idx) => {
            const n = idx + 1;
            const done = step > n;
            const active = step === n;
            return (
              <div key={label} className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    done ? 'bg-emerald-500 text-white' : active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {done ? <i className="ri-check-line text-xs" /> : n}
                </div>
                <span className={`text-sm ${step >= n ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
